/**
 * Lead Generation sub-verticals shown under the Header's "Lead Generation" dropdown.
 * Each vertical has its own /lead-generation/{slug} page rendered by LeadGenDetail.
 *
 * Hero images are loaded from Unsplash CDN at fixed photo IDs. They are layered
 * behind a strong dark gradient so they always match the dark cyber theme.
 */
import { Home, CreditCard, ShieldCheck } from 'lucide-react';

export const LEAD_GEN_VERTICALS = [
  {
    slug: 'real-estate',
    title: 'Real Estate',
    eyebrow: 'Lead Generation · Real Estate',
    short: 'Performance marketing for property developers, brokers, and real-estate platforms — driving qualified buyer and renter leads at scale.',
    icon: Home,
    accent: '#22D3EE',
    heroImage:
      'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=80&auto=format&fit=crop',
    imageCaption: 'Modern residential property',
    summary:
      'Real estate is one of the highest-intent lead generation verticals in performance marketing. Buyers and renters research for weeks or months before submitting a form, and the cost of one qualified lead can determine the entire profitability of a property launch or brokerage campaign. Ads Dashbound supports developers, agencies, and proptech platforms with curated traffic flows tuned for property-form funnels.',
    examples: [
      'New residential project launch campaigns (developer-side)',
      'Brokerage lead capture for active buyer / renter inquiries',
      'Off-plan and pre-construction interest forms',
      'Investment property and second-home GEO targeting',
      'Rental listing portals and short-term-stay sign-ups',
    ],
    howItWorks: [
      {
        title: 'Intent-rich placements',
        body: 'Push, in-page push, and direct click formats catch users actively researching properties on related content sites, finance pages, and lifestyle portals — not generic display banners.',
      },
      {
        title: 'GEO + price-band targeting',
        body: 'Real estate is hyper-local. Source-level controls let you isolate placements by GEO and quickly cut sources that send out-of-region or low-quality leads before they impact your CPL.',
      },
      {
        title: 'Slow-burn funnel respect',
        body: 'Real-estate buyers research over weeks. We size top-ups so the campaign can run long enough for the form to collect meaningful volume — not a 48-hour burn.',
      },
      {
        title: 'Lead quality controls',
        body: 'Fraud filtering and source whitelists protect your CRM from junk leads, fake phone numbers, and bot-generated submissions that ruin agent close rates.',
      },
    ],
    formats: ['push', 'in-page-push', 'pop', 'direct-click', 'domain-redirect'],
    kpiHints: [
      'Cost per qualified lead (CPL / CPQL)',
      'Form completion rate by source',
      'In-region lead ratio (geo-validity)',
      'Agent-confirmed contact rate',
    ],
    compliance: [
      'No misleading “guaranteed approval” or fake property listings',
      'Honest pricing and project disclosures inside the funnel',
      'Compliance with local real-estate advertising regulations (RERA, MLS rules, regional disclaimers)',
      'Respect for opt-in requirements before SMS / WhatsApp follow-up',
    ],
    faq: [
      {
        q: 'Which traffic formats work best for real estate lead generation?',
        a: 'Push and in-page push usually deliver the best balance of cost and quality because they target users mid-research. Direct click and domain redirect can scale further once a winning source list is identified. Pop is used for top-of-funnel discovery in some GEOs.',
      },
      {
        q: 'Can I scale real estate campaigns on a strict CPL?',
        a: 'Yes. Source-level controls let you cap budget per source until lead quality is validated, then scale only placements that hit your CPL and qualified-rate targets.',
      },
      {
        q: 'Do you support hyper-local GEO targeting (city / district)?',
        a: 'GEO granularity depends on traffic format and source. Country and major-city level is available on most formats; finer district-level targeting is best handled via your landing-page filters and form qualifying questions.',
      },
    ],
  },

  {
    slug: 'credit-cards',
    title: 'Finance — Credit Cards',
    eyebrow: 'Lead Generation · Credit Cards',
    short: 'Compliant performance traffic for credit-card issuers, comparison sites, and fintech apps — driving approved-card and pre-qualified leads.',
    icon: CreditCard,
    accent: '#A78BFA',
    heroImage:
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1600&q=80&auto=format&fit=crop',
    imageCaption: 'Premium credit cards',
    summary:
      'Credit-card acquisition is one of the most competitive sub-verticals in performance marketing. Compliance, fraud control, and lead quality are non-negotiable. Ads Dashbound supports issuers, comparison platforms, and fintech apps with curated traffic flows that prioritize clean, in-region, intent-driven applicants.',
    examples: [
      'Issuer-side card application funnels (rewards, travel, cashback)',
      'Pre-qualification “check your eligibility” forms',
      'Credit-card comparison and aggregator marketplaces',
      'Co-branded card promotions (airlines, retailers, fintechs)',
      'Secured / starter card acquisition for credit-builders',
    ],
    howItWorks: [
      {
        title: 'Intent-aligned creative routing',
        body: 'Direct click and push placements direct users to the right card variant (cashback, travel, low-APR) based on inferred intent — improving form completion and approval rates.',
      },
      {
        title: 'Strict source quality controls',
        body: 'Credit-card funnels are a fraud magnet. Fraud filtering, source-level reporting, and aggressive blacklist controls keep bot submissions and synthetic identities out of your application flow.',
      },
      {
        title: 'GEO + tier targeting',
        body: 'Card eligibility differs by country, region, and credit tier. Source-level reports let you spot underperforming GEOs early and reallocate before CPA blows out.',
      },
      {
        title: 'Compliance-first onboarding',
        body: 'Every advertiser application is reviewed for issuer / regulatory compliance, APR disclosure, and accurate offer terms before campaigns go live.',
      },
    ],
    formats: ['push', 'in-page-push', 'direct-click', 'domain-redirect'],
    kpiHints: [
      'Cost per funded / approved card',
      'Pre-qualification → submission rate',
      'Per-source approval rate',
      'Fraud / duplicate submission ratio',
    ],
    compliance: [
      'Full APR, fees, and terms disclosed inside the funnel',
      'No “guaranteed approval” language or misleading credit-score claims',
      'Compliance with local finance advertising regulations (FCA, CFPB, ASIC, etc.)',
      'Sensitive-data handling for income, employment, and SSN-equivalent fields',
    ],
    faq: [
      {
        q: 'Which traffic formats work best for credit-card lead generation?',
        a: 'Push, in-page push, and direct click usually perform best because they catch users mid-research on finance content. Domain redirect can scale further on broader “best credit card” keyword paths once the source list is validated.',
      },
      {
        q: 'Do you support pre-qualification (soft pull) flows?',
        a: 'Yes — soft-pull pre-qualification forms typically convert better than direct application links and protect approval rates. Your account manager will help match the right format to the right offer.',
      },
      {
        q: 'How do you handle fraud and synthetic identities?',
        a: 'We run continuous bot and source-quality filtering and let you whitelist or blacklist sources based on real submission and approval data from your CRM. We do not claim 100% fraud-free traffic — anyone who does should be treated with caution.',
      },
    ],
  },

  {
    slug: 'insurance',
    title: 'Insurance',
    eyebrow: 'Lead Generation · Insurance',
    short: 'Performance traffic for auto, home, health, life, and travel insurance funnels — driving quote-request and policy-sign-up leads.',
    icon: ShieldCheck,
    accent: '#34D399',
    heroImage:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1600&q=80&auto=format&fit=crop',
    imageCaption: 'Protection and coverage',
    summary:
      'Insurance is a classic high-value lead-gen vertical: quotes are slow to convert but lifetime value is high. Ads Dashbound supports brokers, carriers, and comparison platforms with curated, in-region, intent-aligned traffic for quote-request and policy-sign-up funnels.',
    examples: [
      'Auto insurance quote-request funnels',
      'Home and renters insurance lead capture',
      'Term life and final-expense pre-qualification forms',
      'Health and Medicare-supplement comparison sites',
      'Travel and short-term policy sign-ups',
    ],
    howItWorks: [
      {
        title: 'Quote-intent traffic',
        body: 'Push, in-page push, and direct click direct active researchers — drivers comparing renewal premiums, new homeowners, expecting parents — into your quote funnel at the right point in their decision.',
      },
      {
        title: 'Strict GEO + license targeting',
        body: 'Insurance is regulated state-by-state and country-by-country. Source controls and GEO targeting keep traffic inside licensed regions and prevent wasted spend on out-of-state applicants.',
      },
      {
        title: 'Lead routing to the right product',
        body: 'Multi-product insurance brokers can split traffic between auto / home / life / health funnels using format and creative variants, with source-level data to learn what each placement converts best for.',
      },
      {
        title: 'Quality protection for agents',
        body: 'Fraud filtering and source whitelists protect your downstream agent or carrier from junk leads, fake numbers, and quote-shoppers with no real intent to buy.',
      },
    ],
    formats: ['push', 'in-page-push', 'pop', 'direct-click'],
    kpiHints: [
      'Cost per qualified quote',
      'Quote → bound policy conversion',
      'Per-source quote validity ratio',
      'Average policy AOV by source',
    ],
    compliance: [
      'Honest coverage and exclusion disclosures inside the funnel',
      'No misleading “lowest rate guaranteed” claims',
      'Licensed-region targeting only — no out-of-state lead farming',
      'Compliance with regional insurance advertising rules and CAN-SPAM / TCPA where applicable',
    ],
    faq: [
      {
        q: 'Which traffic formats work best for insurance lead generation?',
        a: 'Push and in-page push usually deliver the strongest balance of cost and quality on auto, home, and life funnels. Direct click can scale further once a winning source list is identified. Pop is occasionally used on health / Medicare for broader discovery in certain GEOs.',
      },
      {
        q: 'Can I separate traffic by product line (auto vs home vs life)?',
        a: 'Yes. Creative variants and dedicated landing pages let you split traffic by product. Source-level reporting then shows which placements convert best for each product line.',
      },
      {
        q: 'How do you handle quote-shoppers vs real intent?',
        a: 'Source-level controls let you blacklist underperforming sources after you collect enough policy-bound data. Fraud filtering removes obvious bot / duplicate submissions. We do not promise specific bind rates — those depend on your funnel, pricing, and agent process.',
      },
    ],
  },
];

export const LEAD_GEN_INDEX = {
  title: 'Lead Generation',
  eyebrow: 'Lead Generation',
  intro:
    'Ads Dashbound supports lead-generation advertisers across high-value verticals where every form submission has real downstream value. Each sub-vertical has its own traffic mix, compliance rules, and quality controls.',
};
